import {
    Cell,
    ColumnDef,
    ColumnFiltersState,
    ColumnPinningPosition,
    ColumnPinningState,
    FilterFn,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    OnChangeFn,
    PaginationState,
    PartialKeys,
    type Row,
    RowData,
    RowSelectionState,
    SortingState,
    type Table,
    TableOptionsResolved,
    useReactTable,
} from '@tanstack/react-table'
import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual'
import { Box, css, Stack, styled, Theme, useTheme } from '@mui/material'
import { isEmpty } from 'lodash'
import { isKeyOf, TextWithEllipsis, tokens, Typography } from 'ComponentLibrary'
import { memo, useCallback, useMemo, useRef, useState } from 'react'
import invariant from 'tiny-invariant'
import { TableVariant, TableVariantContext } from './TableVariantContext'
import { IconButton } from 'ComponentLibrary/src/piazza'
import { ArrowLeftIcon, ArrowRightIcon, PencilSimpleIcon, SortAscendingIcon, SortDescendingIcon } from '@phosphor-icons/react'

declare module '@tanstack/react-table' {
    // This is needed to ensure that filterFns stays optional after adding our custom dictionaryFilter as an option
    // ref: https://github.com/TanStack/table/issues/5005#issuecomment-2118846886
    interface TableOptions<TData extends RowData> extends PartialKeys<
        TableOptionsResolved<TData>,
        'state' | 'onStateChange' | 'renderFallbackValue'
    > {
        filterFns?: FilterFns
    }

    interface FilterFns {
        dictionaryFilter: FilterFn<unknown>
    }
}

type CommonTableProps<TData> = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see existing issue: https://github.com/TanStack/table/issues/4382
    columns: ColumnDef<TData, any>[]
    data: TData[]
    variant?: TableVariant
    className?: string
    columnPinning?: ColumnPinningState
    pagination?: Partial<PaginationState>
    maxWidth?: string
    maxHeight?: string
    showNumberOfEntries?: boolean
    disabled?: boolean
    alignHeader?: 'start' | 'center' | 'end'
    columnFilters?: ColumnFiltersState
    setColumnFilters?: OnChangeFn<ColumnFiltersState>
    rowSelection?: RowSelectionState
    setRowSelection?: OnChangeFn<RowSelectionState>
    greyThemed?: boolean
    isFiltered?: boolean
    width?: string
    columnAction?: (index: number) => void
    rowBackgroundColors?: string[]
    withZeroCellPadding?: boolean
    withTableSpacing?: boolean
    withGroupSeparators?: boolean
    getRowId?: (originalRow: TData, index: number, parent?: Row<TData>) => string
    copy?: Partial<TableCopy>
}

type TablePropsWithControlledWidth<TData> = {
    controlledSizing?: false
    enableColumnResizing?: false
} & CommonTableProps<TData>

type TablePropsWithControlledColumns<TData> = {
    controlledSizing: true
    enableColumnResizing?: boolean
} & CommonTableProps<TData>

type TableProps<TData> = TablePropsWithControlledWidth<TData> | TablePropsWithControlledColumns<TData>

type SortDirection = false | 'asc' | 'desc'

export type TableCopy = {
    entriesLabel: (count: number) => string
    selectedEntriesLabel: (selectedCount: number, totalCount: number) => string
    pageLabel: (page: number, pageCount: number) => string
    emptyFiltered: string
    empty: string
}

const DEFAULT_TABLE_COPY: TableCopy = {
    entriesLabel: (count) => `${count} ${count === 1 ? 'Eintrag' : 'Einträge'}`,
    selectedEntriesLabel: (selectedCount, totalCount) =>
        `${selectedCount} von ${totalCount} ${totalCount === 1 ? 'Eintrag' : 'Einträgen'} ausgewählt`,
    pageLabel: (page, pageCount) => `${page} von ${pageCount} Seite`,
    emptyFiltered: 'Keine Einträge zu Ihrer Suche',
    empty: 'Keine Einträge vorhanden',
}

function SortIcon({ sortDirection }: { sortDirection: SortDirection }) {
    const iconColor = sortDirection ? tokens.ColorIconLinkDefault : tokens.ColorIconDefault
    const iconProps = { color: iconColor, size: tokens.SizingIconS, weight: 'bold' as const, style: { flexShrink: 0 } }

    if (sortDirection === 'asc') {
        return <SortAscendingIcon {...iconProps} />
    }
    if (sortDirection === 'desc') {
        return <SortDescendingIcon {...iconProps} />
    }
    return <SortAscendingIcon {...iconProps} />
}

function enableSortingForColumns<TData>(columns: ColumnDef<TData, unknown>[]): ColumnDef<TData, unknown>[] {
    return columns.map((column) => {
        const columnWithSorting = column as ColumnDef<TData, unknown> & {
            columns?: ColumnDef<TData, unknown>[]
            enableSorting?: boolean
            sortingFn?: unknown
        }

        return {
            ...column,
            enableSorting: columnWithSorting.enableSorting ?? !!columnWithSorting.sortingFn,
            ...(columnWithSorting.columns && { columns: enableSortingForColumns(columnWithSorting.columns) }),
        }
    })
}

export function Table<TData>({
    data,
    variant = 'default',
    columns,
    className,
    columnPinning,
    pagination,
    width,
    maxWidth,
    maxHeight,
    showNumberOfEntries,
    controlledSizing,
    enableColumnResizing = false,
    disabled,
    alignHeader,
    columnFilters,
    setColumnFilters,
    rowSelection,
    setRowSelection,
    greyThemed,
    isFiltered,
    columnAction,
    rowBackgroundColors,
    withZeroCellPadding,
    withTableSpacing,
    withGroupSeparators,
    getRowId,
    copy,
}: TableProps<TData>) {
    const theme = useTheme()
    const tableCopy = { ...DEFAULT_TABLE_COPY, ...copy }
    const [columnHoverId, setColumnHoverId] = useState<string | undefined>(undefined)
    const [sorting, setSorting] = useState<SortingState>([])
    const sortableColumns = useMemo(() => enableSortingForColumns(columns), [columns])
    const table = useReactTable({
        data,
        columns: sortableColumns,
        getRowId,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: pagination ? getPaginationRowModel() : undefined,
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        initialState: {
            pagination,
        },
        state: {
            columnFilters,
            rowSelection,
            sorting,
            ...(columnPinning && { columnPinning }),
        },
        onRowSelectionChange: setRowSelection,
        onColumnFiltersChange: setColumnFilters,
        onSortingChange: setSorting,
        filterFns: {
            dictionaryFilter: (row, columnId, filterValue: { [key: string]: boolean }) => {
                if (!Object.values(filterValue).includes(true)) {
                    return true
                }
                invariant(
                    typeof row.original === 'object' && !!row.original && isKeyOf(columnId, row.original),
                    `Unable to access property ${columnId} in object ${JSON.stringify(row.original)}`
                )
                return !!filterValue[row.original[columnId]]
            },
        },
        enableColumnResizing,
        columnResizeMode: 'onChange',
        defaultColumn: {
            minSize: 50,
            maxSize: 500,
        },
    })

    const rowHighlightColor =
        variant === 'simple'
            ? tokens.ColorSurfaceDefault
            : greyThemed
              ? tokens.ColorSurfaceInverseDefault
              : tokens.ColorSurfaceAccentGreenDefault

    const tableState = table.getState()

    const columnSizes = useMemo(() => {
        const headers = table.getFlatHeaders()
        const colSizes: { [key: string]: number } = {}
        for (let i = 0; i < headers.length; i++) {
            const header = headers[i]
            colSizes[`header${header.id}`] = header.getSize()
            colSizes[`col${header.column.id}`] = header.column.getSize()
        }
        return colSizes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableState.columnSizingInfo, table])

    const hasRows = !isEmpty(data)
    // code taken from https://github.com/TanStack/table/discussions/5023#discussioncomment-8572450
    const hasFooters =
        table
            .getFooterGroups()
            .map((group) => group.headers.map((header) => header.column.columnDef.footer))
            .flat()
            .filter(Boolean).length > 0

    const scrollParentRef = useRef<HTMLDivElement | null>(null)
    const allRows = table.getRowModel().rows

    const getItemKey = useCallback((index: number) => {
        return allRows[index]?.id ?? index
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const rowVirtualizer = useVirtualizer({
        count: allRows.length,
        getScrollElement: () => scrollParentRef.current,
        estimateSize: () => (variant === 'simple' ? 32 : 57),
        overscan: 4,
        getItemKey,
    })
    const virtualRows = rowVirtualizer.getVirtualItems()
    const totalSize = rowVirtualizer.getTotalSize()
    const bufferTop = virtualRows.length > 0 ? virtualRows[0].start : 0
    const bufferBottom = virtualRows.length > 0 ? totalSize - virtualRows[virtualRows.length - 1].end : 0

    const numberOfRows = data.length
    const numberOfSelectedRows = Object.keys(tableState.rowSelection ?? {}).length
    const separatorColumnIds = useMemo(() => {
        if (!withGroupSeparators) {
            return []
        }

        const getLastLeafColumnId = (header: ReturnType<typeof table.getFlatHeaders>[number]): string => {
            const lastSubHeader = header.subHeaders.at(-1)
            return lastSubHeader ? getLastLeafColumnId(lastSubHeader) : header.column.id
        }

        const topLevelHeaders = table.getHeaderGroups()[0]?.headers ?? []
        return topLevelHeaders
            .slice(0, -1)
            .filter((header) => (variant === 'simple' ? header.column.id : header.id) !== 'select-col')
            .map(getLastLeafColumnId)
    }, [table, variant, withGroupSeparators])
    const hasGroupSeparator = useCallback(
        (header: ReturnType<typeof table.getFlatHeaders>[number]): boolean => {
            if (!separatorColumnIds.length) {
                return false
            }
            if (header.subHeaders.length === 0) {
                return separatorColumnIds.includes(header.column.id)
            }
            return header.subHeaders.some(hasGroupSeparator)
        },
        [separatorColumnIds]
    )
    const getHeaderLeafHeaders = (header: ReturnType<typeof table.getFlatHeaders>[number]): ReturnType<typeof table.getFlatHeaders> => {
        return header.subHeaders.length ? header.subHeaders.flatMap(getHeaderLeafHeaders) : [header]
    }
    const getHeaderPinning = (header: ReturnType<typeof table.getFlatHeaders>[number]): ColumnPinningPosition | false => {
        const leafHeaders = getHeaderLeafHeaders(header)
        const pinnedPosition = leafHeaders[0]?.column.getIsPinned()

        if (!pinnedPosition || leafHeaders.some((leafHeader) => leafHeader.column.getIsPinned() !== pinnedPosition)) {
            return false
        }

        return pinnedPosition
    }
    const getHeaderLeftOffset = (header: ReturnType<typeof table.getFlatHeaders>[number]) => {
        return getHeaderLeafHeaders(header)[0]?.column.getStart('left')
    }
    const getHeaderRightOffset = (header: ReturnType<typeof table.getFlatHeaders>[number]) => {
        return getHeaderLeafHeaders(header).at(-1)?.column.getAfter('right')
    }

    return (
        <TableVariantContext.Provider value={variant}>
            <div
                ref={scrollParentRef}
                style={{
                    overflowX: 'auto',
                    overflowY: 'auto',
                    maxWidth,
                    maxHeight,
                    pointerEvents: disabled ? 'none' : undefined,
                    opacity: disabled ? '0.5' : undefined,
                }}
                inert={disabled || undefined}
            >
                <StyledTable className={className} width={width} $withTableSpacing={withTableSpacing} $variant={variant}>
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => {
                            const isGroupedHeader = headerGroup.depth !== table.getHeaderGroups().length - 1

                            return (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header, index) => {
                                        const headerPinning = getHeaderPinning(header)

                                        return (
                                            <StyledHeadingCell
                                                $variant={variant}
                                                key={header.id}
                                                $pinned={headerPinning}
                                                $leftOffset={getHeaderLeftOffset(header)}
                                                $rightOffset={getHeaderRightOffset(header)}
                                                $isLastLeftPinned={
                                                    headerPinning === 'left' &&
                                                    getHeaderLeafHeaders(header).at(-1)?.column.getIsLastColumn('left')
                                                }
                                                colSpan={header.colSpan}
                                                $isGroup={isGroupedHeader}
                                                onMouseEnter={columnAction ? () => setColumnHoverId(header.column.id) : undefined}
                                                onMouseLeave={columnAction ? () => setColumnHoverId(undefined) : undefined}
                                                $withZeroCellPadding={withZeroCellPadding}
                                                $withTableSpacing={withTableSpacing}
                                                $withGroupSeparator={hasGroupSeparator(header)}
                                            >
                                                <StyledHeadingContent
                                                    $variant={variant}
                                                    $alignItems={
                                                        variant === 'simple' && header.column.id === 'select-col' ? 'center' : alignHeader
                                                    }
                                                    $width={
                                                        controlledSizing && (!isGroupedHeader || headerPinning)
                                                            ? `${columnSizes[`header${header.id}`]}px`
                                                            : undefined
                                                    }
                                                    $height={columnAction ? tokens.SizingIconM : undefined}
                                                    $withZeroCellPadding={withZeroCellPadding}
                                                >
                                                    {header.isPlaceholder ? null : header.id === 'select-col' ? (
                                                        flexRender(header.column.columnDef.header, header.getContext())
                                                    ) : (
                                                        <SortableHeaderContent
                                                            $sortable={header.column.getCanSort()}
                                                            onClick={header.column.getToggleSortingHandler()}
                                                        >
                                                            <TextWithEllipsis
                                                                typographyProps={{
                                                                    variant: 'captionSmall',
                                                                    style:
                                                                        variant === 'simple'
                                                                            ? isGroupedHeader
                                                                                ? tokens.Headline4
                                                                                : tokens.Headline5
                                                                            : undefined,
                                                                    color: header.column.getIsSorted()
                                                                        ? tokens.ColorTextLinkDefault
                                                                        : !isGroupedHeader
                                                                          ? theme.palette.customtext.black54
                                                                          : variant === 'simple'
                                                                            ? tokens.ColorTextBoldest
                                                                            : theme.palette.customtext.black80,
                                                                }}
                                                                tooltipText={header.column.columnDef.header?.toString() ?? ''}
                                                            >
                                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                            </TextWithEllipsis>
                                                            {header.column.getCanSort() && (
                                                                <SortIconContainer $visible={!!header.column.getIsSorted()}>
                                                                    <SortIcon sortDirection={header.column.getIsSorted()} />
                                                                </SortIconContainer>
                                                            )}
                                                        </SortableHeaderContent>
                                                    )}
                                                    {header.column.getCanResize() && !isGroupedHeader && (
                                                        <Resizer
                                                            onMouseDown={header.getResizeHandler()}
                                                            onTouchStart={header.getResizeHandler()}
                                                        />
                                                    )}
                                                    {columnAction && index !== 0 && header.column.id === columnHoverId ? (
                                                        <Box
                                                            sx={{
                                                                right: 0,
                                                                top: 0,
                                                                position: 'absolute',
                                                                margin: tokens.SizingSpacing025,
                                                                bgcolor: tokens.ColorSurfaceDefault,
                                                                opacity: 0.75,
                                                                '&:hover': {
                                                                    opacity: 1,
                                                                },
                                                            }}
                                                        >
                                                            <IconButton
                                                                variant="tertiary"
                                                                size="small"
                                                                icon={
                                                                    <PencilSimpleIcon
                                                                        weight="bold"
                                                                        size={tokens.SizingIconS}
                                                                        style={{ flexShrink: 0 }}
                                                                    />
                                                                }
                                                                onClick={() => columnAction(index - 1)}
                                                            />
                                                        </Box>
                                                    ) : null}
                                                </StyledHeadingContent>
                                            </StyledHeadingCell>
                                        )
                                    })}
                                </tr>
                            )
                        })}
                    </thead>
                    {tableState.columnSizingInfo.isResizingColumn ? (
                        <MemoizedTableBody
                            variant={variant}
                            table={table}
                            hasRows={hasRows}
                            columnSizes={columnSizes}
                            controlledSizing={controlledSizing}
                            rowHighlightColor={rowHighlightColor}
                            setColumnHoverId={columnAction && setColumnHoverId}
                            rowBackgroundColors={rowBackgroundColors}
                            virtual={{ bufferTop, bufferBottom, virtualRows, measureElement: rowVirtualizer.measureElement }}
                            withZeroCellPadding={withZeroCellPadding}
                            withTableSpacing={withTableSpacing}
                            hasColumnGroupSeparator={(columnId) => separatorColumnIds.includes(columnId)}
                            copy={tableCopy}
                        />
                    ) : (
                        <TableBody
                            variant={variant}
                            table={table}
                            hasRows={hasRows}
                            columnSizes={columnSizes}
                            controlledSizing={controlledSizing}
                            rowHighlightColor={rowHighlightColor}
                            isFiltered={isFiltered}
                            setColumnHoverId={columnAction && setColumnHoverId}
                            rowBackgroundColors={rowBackgroundColors}
                            virtual={{ bufferTop, bufferBottom, virtualRows, measureElement: rowVirtualizer.measureElement }}
                            withZeroCellPadding={withZeroCellPadding}
                            withTableSpacing={withTableSpacing}
                            hasColumnGroupSeparator={(columnId) => separatorColumnIds.includes(columnId)}
                            copy={tableCopy}
                        />
                    )}

                    {table.getRowModel().rows.length > 0 && hasFooters && (
                        <tfoot>
                            {table.getFooterGroups().map((footerGroup, groupIndex) => {
                                if (groupIndex !== 0) {
                                    return null
                                }
                                return (
                                    <StyledFooterRow
                                        key={footerGroup.id}
                                        $rowHighlightColor={
                                            variant === 'simple' ? tokens.ColorSurfaceDefault : tokens.ColorSurfaceAccentGreenDefault
                                        }
                                    >
                                        {footerGroup.headers.map((header) => (
                                            <StyledFooterHeader
                                                $variant={variant}
                                                scope="row"
                                                key={header.id}
                                                $pinned={header.column.getIsPinned()}
                                                $leftOffset={header.column.getStart('left')}
                                                $rightOffset={header.column.getAfter('right')}
                                                $withZeroCellPadding={withZeroCellPadding}
                                                $withTableSpacing={withTableSpacing}
                                                $withGroupSeparator={separatorColumnIds.includes(header.column.id)}
                                            >
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(header.column.columnDef.footer, header.getContext())}
                                            </StyledFooterHeader>
                                        ))}
                                    </StyledFooterRow>
                                )
                            })}
                        </tfoot>
                    )}
                </StyledTable>
            </div>
            {(showNumberOfEntries || pagination) && (
                <Stack direction="row" justifyContent="space-between" width={width} marginTop={tokens.SizingSpacing150} alignItems="center">
                    {showNumberOfEntries && (
                        <Typography variant="bodyTextSmall">
                            {numberOfSelectedRows > 0
                                ? tableCopy.selectedEntriesLabel(numberOfSelectedRows, numberOfRows)
                                : tableCopy.entriesLabel(numberOfRows)}
                        </Typography>
                    )}
                    {pagination && (
                        <Stack direction="row" marginLeft="auto" gap={tokens.SizingSpacing075}>
                            <IconButton
                                icon={<ArrowLeftIcon />}
                                onClick={table.previousPage}
                                disabled={!table.getCanPreviousPage()}
                                size={'small'}
                                variant="secondary"
                            />
                            <Stack alignItems="center" gap={theme.spacing(0.5)} direction="row">
                                <Typography variant="bodyTextSmall">
                                    {tableCopy.pageLabel(table.getState().pagination.pageIndex + 1, table.getPageCount() || 1)}
                                </Typography>
                            </Stack>
                            <IconButton
                                icon={<ArrowRightIcon />}
                                onClick={table.nextPage}
                                disabled={!table.getCanNextPage()}
                                size={'small'}
                                variant="secondary"
                            />
                        </Stack>
                    )}
                </Stack>
            )}
        </TableVariantContext.Provider>
    )
}

type TableBodyProps<TData> = {
    variant: TableVariant
    table: Table<TData>
    hasRows: boolean
    columnSizes: { [key: string]: number }
    controlledSizing?: boolean
    rowHighlightColor: string
    isFiltered?: boolean
    setColumnHoverId?: (id: string | undefined) => void
    rowBackgroundColors?: string[]
    withZeroCellPadding?: boolean
    withTableSpacing?: boolean
    hasColumnGroupSeparator?: (columnId: string) => boolean
    copy: TableCopy
    virtual: {
        bufferTop: number
        bufferBottom: number
        virtualRows: VirtualItem[]
        measureElement: (element: Element | null) => void
    }
}

function BodyCell<TData>({
    variant,
    cell,
    columnSizes,
    controlledSizing,
    setColumnHoverId,
    withZeroCellPadding,
    withTableSpacing,
    withGroupSeparator,
}: {
    variant: TableVariant
    cell: Cell<TData, unknown>
    columnSizes: { [key: string]: number }
    controlledSizing?: boolean
    setColumnHoverId?: (id: string | undefined) => void
    withZeroCellPadding?: boolean
    withTableSpacing?: boolean
    withGroupSeparator?: boolean
}) {
    return (
        <StyledBodyCell
            $variant={variant}
            $isSelection={variant === 'simple' && cell.column.id === 'select-col'}
            $pinned={cell.column.getIsPinned()}
            $leftOffset={cell.column.getStart('left')}
            $rightOffset={cell.column.getAfter('right')}
            $isLastLeftPinned={cell.column.getIsPinned() === 'left' && cell.column.getIsLastColumn('left')}
            $width={controlledSizing ? `${columnSizes[`col${cell.column.id}`]}px` : undefined}
            onMouseEnter={() => setColumnHoverId?.(cell.column.id)}
            onMouseLeave={() => setColumnHoverId?.(undefined)}
            $withZeroCellPadding={withZeroCellPadding}
            $withTableSpacing={withTableSpacing}
            $withGroupSeparator={withGroupSeparator}
        >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </StyledBodyCell>
    )
}

function TableBody<TData>({
    variant,
    table,
    hasRows,
    columnSizes,
    controlledSizing,
    rowHighlightColor,
    isFiltered,
    rowBackgroundColors,
    virtual,
    setColumnHoverId,
    withZeroCellPadding,
    withTableSpacing,
    hasColumnGroupSeparator,
    copy,
}: TableBodyProps<TData>) {
    const emptyText = isFiltered ? copy.emptyFiltered : copy.empty
    const rows = table.getRowModel().rows

    if (!hasRows) {
        return (
            <tbody>
                <StyledBodyRow index={0} $rowHighlightColor={rowHighlightColor}>
                    <StyledBodyCell
                        $variant={variant}
                        colSpan={table.getAllLeafColumns().length}
                        $withZeroCellPadding={withZeroCellPadding}
                        $withTableSpacing={withTableSpacing}
                    >
                        <EmptyStateCell>{emptyText}</EmptyStateCell>
                    </StyledBodyCell>
                </StyledBodyRow>
            </tbody>
        )
    }

    return (
        <tbody>
            {virtual.bufferTop > 0 && (
                <tr>
                    <td colSpan={table.getAllLeafColumns().length} style={{ height: `${virtual.bufferTop}px` }} />
                </tr>
            )}

            {virtual.virtualRows.map((vRow) => {
                const row = rows[vRow.index]
                return (
                    <StyledBodyRow
                        ref={variant === 'simple' ? virtual.measureElement : undefined}
                        data-index={vRow.index}
                        index={vRow.index}
                        key={row.id}
                        $rowHighlightColor={rowHighlightColor}
                        $customBackgroundColor={rowBackgroundColors?.[vRow.index]}
                    >
                        {row.getVisibleCells().map((cell) => (
                            <BodyCell
                                variant={variant}
                                key={cell.id}
                                cell={cell}
                                columnSizes={columnSizes}
                                controlledSizing={controlledSizing}
                                setColumnHoverId={setColumnHoverId}
                                withZeroCellPadding={withZeroCellPadding}
                                withTableSpacing={withTableSpacing}
                                withGroupSeparator={hasColumnGroupSeparator?.(cell.column.id)}
                            />
                        ))}
                    </StyledBodyRow>
                )
            })}

            {virtual.bufferBottom > 0 && (
                <tr>
                    <td colSpan={table.getAllLeafColumns().length} style={{ height: `${virtual.bufferBottom}px` }} />
                </tr>
            )}
        </tbody>
    )
}

const MemoizedTableBody = memo(TableBody, (prev, next) => prev.table.options.data === next.table.options.data) as typeof TableBody

const StyledTable = styled('table', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $withTableSpacing?: boolean; $variant: TableVariant }>(({ $withTableSpacing, $variant }) => ({
    '--table-cell-padding-inline': $variant === 'simple' ? tokens.SizingSpacing100 : tokens.SizingSpacing200,
    '--table-cell-padding-block': $variant === 'simple' ? '0px' : tokens.SizingSpacing100,
    '--table-border-color': $variant === 'simple' ? tokens.ColorBorderDefault : tokens.ColorSurfaceDefault,
    '--table-footer-border-color': $variant === 'simple' ? tokens.ColorBorderDefault : tokens.ColorBorderSelectedDefault,
    paddingBottom: '2px',
    borderSpacing: $withTableSpacing ? '2px 0' : 0,
    '& tbody > tr:hover > td':
        $variant === 'simple'
            ? { backgroundImage: `linear-gradient(${tokens.ColorSurfaceHover}, ${tokens.ColorSurfaceHover})` }
            : undefined,
    '& thead': {
        backgroundColor: $variant === 'simple' ? tokens.ColorSurfaceDefault : undefined,
        position: 'sticky',
        top: 0,
        zIndex: 3,
    },
}))

const StyledHeadingCell = styled('th', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{
    $variant: TableVariant
    $isGroup?: boolean
    $pinned?: ColumnPinningPosition
    $leftOffset?: number
    $rightOffset?: number
    $isLastLeftPinned?: boolean
    $withZeroCellPadding?: boolean
    $withTableSpacing?: boolean
    $withGroupSeparator?: boolean
}>(
    ({
        theme,
        $variant,
        $isGroup,
        $pinned,
        $leftOffset,
        $rightOffset,
        $isLastLeftPinned,
        $withZeroCellPadding,
        $withTableSpacing,
        $withGroupSeparator,
    }) => ({
        padding: 0,
        paddingBottom: $withZeroCellPadding || $variant === 'simple' ? 0 : $isGroup ? `${theme.spacing(1)}` : `10px`,
        borderBottom: $variant === 'simple' || !$isGroup ? `${tokens.SizingBorderS} solid var(--table-border-color)` : undefined,
        '&:not(:first-of-type)': {
            borderLeft: $withTableSpacing || $variant === 'simple' ? undefined : `1px solid ${theme.palette.white}`,
        },
        ...($withGroupSeparator &&
            !$isLastLeftPinned && {
                borderRight: `${tokens.SizingBorderM} solid ${tokens.ColorBorderDefault}`,
            }),
        ...($pinned && {
            position: 'sticky',
            zIndex: 1,
            ...($pinned === 'right' && { right: $rightOffset }),
            ...($pinned === 'left' && { left: $leftOffset }),
        }),
        ...($isLastLeftPinned && {
            boxShadow: `inset -${$withGroupSeparator ? tokens.SizingBorderM : tokens.SizingBorderS} 0 0 ${
                $withGroupSeparator ? tokens.ColorBorderDefault : theme.palette.white
            }`,
        }),
        backgroundColor: theme.palette.white,
    })
)

const StyledHeadingContent = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $width?: string; $height?: string; $alignItems?: string; $withZeroCellPadding?: boolean; $variant: TableVariant }>(
    ({ $width, $height, $alignItems, $withZeroCellPadding, $variant }) => ({
        position: 'relative',
        paddingLeft: $withZeroCellPadding ? 0 : 'var(--table-cell-padding-inline)',
        paddingRight: $withZeroCellPadding ? 0 : 'var(--table-cell-padding-inline)',
        minHeight: $variant === 'simple' ? tokens.SizingSpacing400 : undefined,
        width: $width ?? undefined,
        height: $height ?? undefined,
        alignItems: $variant === 'simple' ? 'center' : 'flex-end',
        gap: tokens.SizingSpacing050,
        display: 'flex',
        justifyContent: $alignItems,
    })
)

const SortableHeaderContent = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $sortable: boolean }>(({ $sortable }) => ({
    alignItems: 'center',
    cursor: $sortable ? 'pointer' : undefined,
    display: 'flex',
    gap: tokens.SizingSpacing050,
    minWidth: 0,
    position: 'relative',
    width: '100%',
}))

const SortIconContainer = styled('span', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $visible: boolean }>(({ $visible }) => ({
    alignItems: 'center',
    backgroundColor: tokens.ColorSurfaceDefault,
    display: 'flex',
    height: tokens.SizingIconS,
    justifyContent: 'center',
    opacity: $visible ? 1 : 0,
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: `calc(${tokens.SizingIconS} + 2px)`,
    'th:hover &': {
        opacity: 1,
    },
}))

const StyledBodyRow = styled('tr', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ index: number; $rowHighlightColor: string; $customBackgroundColor?: string }>(
    ({ theme, index, $rowHighlightColor, $customBackgroundColor }) => ({
        '& > td': {
            backgroundColor: $customBackgroundColor ?? (index % 2 === 0 ? $rowHighlightColor : theme.palette.white),
        },
    })
)

const StyledFooterRow = styled('tr', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $rowHighlightColor: string }>(({ $rowHighlightColor }) => ({
    '& > th': {
        backgroundColor: $rowHighlightColor,
        borderTop: `${tokens.SizingBorderS} solid var(--table-footer-border-color)`,
    },
}))

type CellProps = {
    $variant: TableVariant
    $isSelection?: boolean
    $pinned?: ColumnPinningPosition
    $width?: string
    $leftOffset?: number
    $rightOffset?: number
    $isLastLeftPinned?: boolean
    $withZeroCellPadding?: boolean
    $withTableSpacing?: boolean
    $withGroupSeparator?: boolean
}

const sharedBodyCellStyles = ({
    theme,
    $variant,
    $isSelection,
    $pinned,
    $width,
    $leftOffset,
    $rightOffset,
    $isLastLeftPinned,
    $withZeroCellPadding,
    $withTableSpacing,
    $withGroupSeparator,
}: CellProps & { theme: Theme }) =>
    css({
        textAlign: $isSelection ? 'center' : 'right',
        ...($variant === 'simple' ? tokens.TextS : theme.typography.bodyTextSmall),
        padding: $withZeroCellPadding ? 0 : 'var(--table-cell-padding-block) var(--table-cell-padding-inline)',
        height: $variant === 'simple' ? tokens.SizingSpacing400 : '40px',
        boxSizing: $variant === 'simple' ? 'border-box' : 'content-box',
        borderBottom: $withTableSpacing ? undefined : `${tokens.SizingBorderS} solid var(--table-border-color)`,
        whiteSpace: 'nowrap',
        '&& .MuiTypography-textXS': $variant === 'simple' ? { fontSize: 'inherit', lineHeight: 'inherit' } : undefined,
        '&:not(:first-of-type)': {
            borderLeft: $withTableSpacing || $variant === 'simple' ? undefined : `1px solid ${theme.palette.white}`,
        },
        ...($withGroupSeparator &&
            !$isLastLeftPinned && {
                borderRight: `${tokens.SizingBorderM} solid ${tokens.ColorBorderDefault}`,
            }),
        ...($pinned && {
            position: 'sticky',
            zIndex: 1,
            ...($pinned === 'right' && { right: $rightOffset }),
            ...($pinned === 'left' && { left: $leftOffset }),
        }),
        ...($isLastLeftPinned && {
            boxShadow: `inset -${$withGroupSeparator ? tokens.SizingBorderM : tokens.SizingBorderS} 0 0 ${
                $withGroupSeparator ? tokens.ColorBorderDefault : theme.palette.white
            }`,
        }),
        maxWidth: $width ?? undefined,
        overflow: 'hidden',
    })

const StyledBodyCell = styled('td', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<CellProps>(sharedBodyCellStyles)

const StyledFooterHeader = styled('th', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<CellProps>(sharedBodyCellStyles)

const EmptyStateCell = styled('div')(({ theme }) => ({
    width: '100%',
    textAlign: 'start',
    color: theme.palette.customtext.black54,
}))

const Resizer = styled('div')(() => ({
    position: 'absolute',
    right: 0,
    top: 0,
    marginRight: '-4px',
    zIndex: 1,
    width: '7px',
    height: '27.5px',
    cursor: 'col-resize',
    userSelect: 'none',
    touchAction: 'none',
}))
